import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PlusCircle, ArrowUpRight, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { StatCard, StatusBadge, usePoll, fmtUSDT, fmtINR } from "@/components/common";
import PaymentConfirm from "@/components/PaymentConfirm";

export default function Dashboard() {
  const [d, setD] = useState(null);
  const nav = useNavigate();
  const load = () => api.get("/customer/dashboard").then((r) => setD(r.data)).catch(() => {});
  usePoll(load, 6000, []);

  if (!d) return <div className="text-slate-400">Loading dashboard…</div>;
  const w = d.wallet;
  const deficit = parseFloat(w.deficit) > 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Your live USDT wallet and INR activity</p>
        </div>
        <div className="flex gap-2">
          <Link to="/app/add-usdt" data-testid="dash-add-usdt-button" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition">
            <PlusCircle className="w-4 h-4" /> Add USDT
          </Link>
          <Link to="/app/sell" data-testid="dash-sell-usdt-button" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition">
            <ArrowUpRight className="w-4 h-4" /> Sell USDT
          </Link>
        </div>
      </div>

      {deficit && (
        <div className="flex items-center gap-3 bg-rose-50 border border-rose-200 rounded-xl p-4 text-rose-800 text-sm" data-testid="deficit-banner">
          <AlertTriangle className="w-5 h-5 shrink-0" />
          You have a USDT deficit of <strong className="font-mono">{fmtUSDT(w.deficit)}</strong>. New sell orders are blocked until it is cleared by a deposit.
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard testid="available-usdt-card" label="Available USDT" value={fmtUSDT(w.available)} tone="blue" />
        <StatCard testid="held-usdt-card" label="Held / Reserved USDT" value={fmtUSDT(w.held)} tone="amber" />
        <StatCard testid="deficit-usdt-card" label="Deficit (negative)" value={fmtUSDT(w.deficit)} tone={deficit ? "rose" : "default"} />
        <StatCard testid="sell-rate-card" label={`Sell Rate · ${d.rate.source}`} value={fmtINR(d.rate.rate)} sub={d.rate.updated_at ? `Updated ${new Date(d.rate.updated_at).toLocaleString()}` : null} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        <StatCard testid="expected-inr-card" label="Expected INR (open)" value={fmtINR(d.expected_inr)} />
        <StatCard testid="paid-inr-card" label="Confirmed INR paid" value={fmtINR(d.confirmed_inr_paid)} tone="green" />
        <StatCard testid="remaining-inr-card" label="Remaining INR receivable" value={fmtINR(d.remaining_inr)} />
      </div>

      {d.pending_confirmations.length > 0 && (
        <div className="space-y-3">
          <h2 className="font-heading font-semibold text-slate-900">Pending payment confirmations</h2>
          {d.pending_confirmations.map((s) => (
            <PaymentConfirm key={s.segment_id} segment={s} orderId={s.order_id} onDone={load} />
          ))}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <h2 className="font-heading font-semibold text-slate-900 mb-3">Recent orders</h2>
          {d.recent_orders.length === 0 ? <Empty text="No orders yet" /> : (
            <div className="space-y-2" data-testid="recent-orders-list">
              {d.recent_orders.map((o) => (
                <Link to={`/app/orders/${o.order_id}`} key={o.order_id} className="flex items-center justify-between p-3 rounded-xl hover:bg-slate-50 transition">
                  <div>
                    <div className="font-mono text-sm text-slate-900">{o.order_id}</div>
                    <div className="text-xs text-slate-500">{fmtUSDT(o.amount)} USDT → {fmtINR(o.expected_inr)}</div>
                  </div>
                  <StatusBadge status={o.status} />
                </Link>
              ))}
            </div>
          )}
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <h2 className="font-heading font-semibold text-slate-900 mb-3">Recent deposits</h2>
          {d.recent_deposits.length === 0 ? <Empty text="No deposits yet" /> : (
            <div className="space-y-2" data-testid="recent-deposits-list">
              {d.recent_deposits.map((dep) => (
                <div key={dep.id} className="flex items-center justify-between p-3 rounded-xl hover:bg-slate-50 transition">
                  <div>
                    <div className="font-mono text-sm text-slate-900">{dep.deposit_ref}</div>
                    <div className="text-xs text-slate-500">{fmtUSDT(dep.amount)} USDT · {dep.network}</div>
                  </div>
                  <StatusBadge status={dep.status} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Empty({ text }) {
  return <div className="text-sm text-slate-400 py-6 text-center">{text}</div>;
}
